import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const HowItWorks = () => {
  const { t } = useLanguage();

  const visuals = [
    {
      number: '01',
      title: 'Choose Tanker Size',
      description: 'Select the capacity you need. 18-ton for residential, 32-ton for commercial and industrial sites.',
      visual: (
        <div className="relative h-32 flex items-center justify-center">
          <div className="flex gap-4">
            <div className="w-20 h-16 rounded-xl bg-watrly-turquoise/20 flex flex-col items-center justify-center border-2 border-watrly-turquoise">
              <span className="text-lg font-bold text-watrly-turquoise">18</span>
              <span className="text-xs text-watrly-turquoise/70">TON</span>
            </div>
            <div className="w-24 h-20 rounded-xl bg-watrly-slate/5 flex flex-col items-center justify-center border-2 border-watrly-slate/20">
              <span className="text-xl font-bold text-watrly-slate/40">32</span>
              <span className="text-xs text-watrly-slate/40">TON</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: '02',
      title: 'Nearest Station Located',
      description: 'Our system automatically identifies the closest water station with available capacity and assigns your order.',
      visual: (
        <div className="relative h-32 flex items-center justify-center">
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-2 border-dashed border-watrly-turquoise/30 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full border-2 border-dashed border-watrly-turquoise/50 flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-watrly-turquoise animate-ping absolute" />
                <div className="w-4 h-4 rounded-full bg-watrly-turquoise" />
              </div>
            </div>
            <div className="absolute top-2 right-0 w-3 h-3 rounded-full bg-watrly-aqua" />
            <div className="absolute bottom-4 left-0 w-2 h-2 rounded-full bg-watrly-aqua-mint" />
            <div className="absolute top-1/2 -right-2 w-2 h-2 rounded-full bg-watrly-turquoise-light" />
          </div>
        </div>
      ),
    },
    {
      number: '03',
      title: 'Driver Assigned',
      description: 'A verified driver is dispatched from the station. You receive their details and vehicle information instantly.',
      visual: (
        <div className="relative h-32 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-watrly-turquoise/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-watrly-turquoise" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex flex-col gap-1">
              <div className="h-3 w-24 rounded-full bg-watrly-slate/20" />
              <div className="h-2 w-16 rounded-full bg-watrly-slate/10" />
              <div className="flex items-center gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs text-green-600 font-medium">Assigned</span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: '04',
      title: 'Live Tracking',
      description: 'Track your tanker in real-time from station to your location. Status updates at every stage of delivery.',
      visual: (
        <div className="relative h-32 flex items-center justify-center">
          <div className="relative w-full max-w-[200px]">
            <div className="h-1 bg-watrly-slate/10 rounded-full w-full" />
            <div className="h-1 bg-watrly-turquoise rounded-full w-3/4 absolute top-0 left-0" />
            <div className="absolute -top-3 left-0 w-6 h-6 rounded-full bg-watrly-turquoise/20 flex items-center justify-center">
              <div className="w-3 h-3 rounded-sm bg-watrly-turquoise" />
            </div>
            <motion.div 
              className="absolute -top-4 w-8 h-8 rounded-full bg-watrly-turquoise flex items-center justify-center shadow-lg"
              style={{ left: 'calc(75% - 16px)' }}
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1V5a1 1 0 00-1-1H3z" />
              </svg>
            </motion.div>
            <div className="absolute -top-3 right-0 w-6 h-6 rounded-full bg-watrly-slate/10 flex items-center justify-center">
              <svg className="w-3 h-3 text-watrly-slate/40" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section id="how-it-works" className="section-padding bg-gradient-to-b from-watrly-sand-light via-watrly-sand-warm/40 to-watrly-cream relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(10,186,181,0.08),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(201,184,150,0.15),transparent_50%)]" />
      
      <div className="container-watrly relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16 lg:mb-24"
        >
          <span className="inline-block text-sm font-semibold text-watrly-turquoise uppercase tracking-widest mb-4">
            {t.howItWorks.badge}
          </span>
          <h2 className="heading-section mb-6">
            {t.howItWorks.title}
          </h2>
          <p className="text-body-lg">
            {t.howItWorks.subtitle}
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-watrly-turquoise/20 via-watrly-turquoise/40 to-watrly-turquoise/20" />

          <div className="space-y-6 md:space-y-8 lg:space-y-0">
            {t.howItWorks.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative lg:grid lg:grid-cols-2 lg:gap-16 items-center ${
                  index % 2 === 0 ? '' : 'lg:direction-rtl'
                }`}
              >
                <div 
                  className={`mb-8 lg:mb-0 ${
                    index % 2 === 0 ? 'lg:text-right lg:pr-16' : 'lg:order-2 lg:text-left lg:pl-16'
                  }`}
                  style={{ direction: 'ltr' }}
                >
                  <div className={`inline-flex items-center gap-4 mb-4 ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                    <span className="text-5xl font-bold text-watrly-turquoise/20">
                      {step.number}
                    </span>
                    <div className="h-px w-12 bg-watrly-turquoise/30" />
                  </div>
                  <h3 className="text-2xl font-semibold text-watrly-slate mb-3">
                    {step.title}
                  </h3>
                  <p className="text-watrly-slate/60 leading-relaxed max-w-md">
                    {step.description}
                  </p>
                </div>

                <div 
                  className={`relative ${
                    index % 2 === 0 ? 'lg:pl-16' : 'lg:order-1 lg:pr-16'
                  }`}
                  style={{ direction: 'ltr' }}
                >
                  <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-4 border-watrly-turquoise items-center justify-center z-10 shadow-lg">
                    <div className="w-3 h-3 rounded-full bg-watrly-turquoise" />
                  </div>

                  <div className="card-glass p-8">
                    {visuals[index]?.visual}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
